'use client'

import React, { SyntheticEvent, useEffect, useRef, useState, useId } from 'react'
import Select, { SelectInstance } from 'react-select'
import { SelectComponents } from 'react-select/dist/declarations/src/components'
import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager'
import useInputWrapper from '../../../hooks/useInputWrapper'
import {
    ClearIndicatorReplacement,
    DropdownIndicatorReplacement,
    MultiLineOption,
    SingleValueMultiLineComponent,
    SingleValueTagComponent,
} from './Components'
import { OptionReplacement } from './Components/OptionReplacement'
import {
    SelectOption,
    GroupedSelectOption,
    SingleSelectInputProps,
    NewSingleSelectInputProps,
} from '../../../types/SelectInput'
import {
    getSelectInputClassNames,
    getSelectContainerClassNames,
    getCustomSelectInputStyles,
    getFilterOption,
} from './Helpers/select-helpers'
import { Text } from '../../atoms'

const getSelectProps = ({
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onBlur,
    isInline = false,
    placeholder,
    noOptionsMessage = 'No results',
    autoFocus = false,
}: SingleSelectInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled)
    const randomId = useId()
    const selectId = id ?? randomId

    const components: Partial<SelectComponents<SelectOption, boolean, GroupedSelectOption>> = {
        DropdownIndicator: DropdownIndicatorReplacement,
        ClearIndicator: ClearIndicatorReplacement,
        ...(!showMultiLine && !showAsTag && { Option: OptionReplacement }),
    }

    if (showAsTag) components.SingleValue = SingleValueTagComponent
    if (showMultiLine) components.SingleValue = SingleValueMultiLineComponent

    const selectProps: StateManagerProps<SelectOption, boolean, GroupedSelectOption> = {
        instanceId: selectId,
        id: selectId,
        inputId: selectId,
        className: getSelectInputClassNames({ isInline, isMulti: false, isSearchable, showAsTag, readOnly }),
        classNamePrefix: 'select-input',
        isDisabled: disabled,
        closeMenuOnSelect: true,
        hideSelectedOptions: false,
        isSearchable: !readOnly ? isSearchable : false,
        onBlur: (event: SyntheticEvent) => {
            if (onBlur) onBlur(event)
            onBlurHandler()
        },
        menuPlacement: 'auto',
        noOptionsMessage: () => <Text>{noOptionsMessage}</Text>,
        components,
        styles: getCustomSelectInputStyles(showMultiLine, isSearchable),
        filterOption: (option, input) => getFilterOption(option, input, showMultiLine),
        ...(showMultiLine && { formatOptionLabel: MultiLineOption }),
        ...(readOnly && { menuIsOpen: false }),
        ...(placeholder ? { placeholder } : { placeholder: '' }),
        autoFocus,
    }

    return {
        selectProps,
        onFocusHandler,
        classes,
    }
}

/**
 * This version of SingleSelectInput works in Admin, but not Account.
 */
export const SingleSelectInput = ({
    options = [],
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultValue,
    isInline = false,
    className = '',
    placeholder,
    noOptionsMessage = 'No results',
    autoFocus = false,
    isClearable = false,
    isLoading,
    menuPosition = 'fixed',
    /** @todo This prop needs to be provided in all use-cases if applicable for accessibility */
    hasError = false,
    ...props
}: SingleSelectInputProps) => {
    const { classes, selectProps, onFocusHandler } = getSelectProps({
        options,
        disabled,
        readOnly,
        isSearchable,
        showAsTag,
        showMultiLine,
        id,
        onChange,
        onBlur,
        value,
        defaultValue,
        isInline,
        className,
        placeholder,
        noOptionsMessage,
        autoFocus,
        isClearable,
        isLoading,
        menuPosition,
        hasError,
    })

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
        >
            <Select
                {...selectProps}
                defaultValue={defaultValue}
                isClearable={!readOnly && isClearable}
                options={options}
                value={options.filter((option) => value === option.value)}
                onFocus={onFocusHandler}
                onChange={(option) => onChange && onChange(option as SelectOption & SelectOption[])}
                openMenuOnClick={!readOnly}
                isLoading={isLoading}
                menuPosition={menuPosition}
                aria-invalid={hasError}
                isOptionDisabled={(option) => !!option.disabled}
            />
        </div>
    )
}

/**
 * @description
 * This version of SingleSelectInput works in Account, but not Admin. This is a 'quick fix' and eventually all projects should be able to use the same component.
 * How this component differs from SingleSelectInput: 'value' no longer gets passed to Select, defaultValue is determined from options, the 'name' property gets passed to Select.
 */
export const NewSingleSelectInput = ({
    options = [],
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultValue,
    isInline = false,
    className = '',
    placeholder,
    noOptionsMessage = 'No results',
    autoFocus = false,
    isClearable = false,
    isLoading,
    menuPosition = 'fixed',
    /** @todo This prop needs to be provided in all use-cases if applicable for accessibility */
    hasError = false,
    name,
    required = false,
    ...props
}: NewSingleSelectInputProps) => {
    const { classes, selectProps, onFocusHandler } = getSelectProps({
        options,
        disabled,
        readOnly,
        isSearchable,
        showAsTag,
        showMultiLine,
        id,
        onChange,
        onBlur,
        value,
        defaultValue,
        isInline,
        className,
        placeholder,
        noOptionsMessage,
        autoFocus,
        isClearable,
        isLoading,
        menuPosition,
        hasError,
    })
    const componentRef = useRef<SelectInstance<SelectOption>>(null)
    const selectRef = props.selectInputRef ?? componentRef
    const hiddenInputRef = useRef<HTMLInputElement>(null)

    // Track if the component has mounted to avoid unnecessary re-renders
    const hasMounted = useRef(false)

    // Generate a unique ID for this instance
    const instanceId = useRef(`select-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`)

    // Track the controlled value separately from defaultValue
    const [controlledValue, setControlledValue] = useState<string | number | null>(defaultValue)

    // Update the controlled value when defaultValue changes
    useEffect(() => {
        // Only update if this isn't the initial mount
        if (hasMounted.current) {
            setControlledValue(defaultValue)
        } else {
            hasMounted.current = true
        }
    }, [defaultValue])

    // Effect to handle programmatic clearing
    useEffect(() => {
        if (controlledValue === null && selectRef.current) {
            selectRef.current.clearValue()

            if (hiddenInputRef.current) {
                hiddenInputRef.current.value = ''
            }
        }
    }, [controlledValue])

    const handleChange = (option: any) => {
        setControlledValue(option?.value || null)

        if (hiddenInputRef.current) {
            hiddenInputRef.current.value = option?.value?.toString() || ''

            const event = new Event('change', { bubbles: true })
            hiddenInputRef.current.dispatchEvent(event)
        }

        if (onChange) {
            onChange(option, { action: 'select-option', option: option })
        }
    }

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
            key={`${instanceId.current}-${isLoading ? 'loading' : 'normal'}`}
        >
            {/* Hidden input for form submission */}
            <input
                ref={hiddenInputRef}
                type="hidden"
                name={name}
                id={`${id || name}-hidden`}
                value={controlledValue?.toString() || ''}
            />
            <Select
                {...selectProps}
                ref={selectRef}
                key={`select-${controlledValue === null ? 'null' : controlledValue}`}
                defaultValue={options?.find((option) => controlledValue === option.value) || null}
                isClearable={!readOnly && isClearable}
                options={options}
                onFocus={onFocusHandler}
                onChange={handleChange}
                openMenuOnClick={!readOnly}
                isLoading={isLoading}
                menuPosition={menuPosition}
                aria-invalid={hasError}
                name={name}
                isOptionDisabled={(option) => !!option.disabled}
                required={required}
            />
        </div>
    )
}
